import { useState } from 'react';
import AttachmentPreviewContent, { getFileExtension } from 'components/Dashboard/components/AttachmentPreviewContent';
import SharedPdfPreview from 'components/ShareDocument/SharedPdfPreview';
import { extensionFromContentType } from 'components/UploadHub/fileTypeBadges';

export const withExtension = ({ fileName, contentType }: { fileName?: string; contentType?: string }) => {
  if (!fileName || /\.[^./]+$/.test(fileName)) return fileName;
  const ext = extensionFromContentType(contentType);
  return ext ? `${fileName}.${ext}` : fileName;
};

export interface SharedDocumentData {
  documentUrl?: string;
  fileName?: string;
  contentType?: string;
  backgroundColor?: string;
  organisationLogo?: string;
}

const SharedDocumentPreview = ({ document: sharedDocument }: { document: SharedDocumentData }) => {
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const fileNameWithExtension = withExtension({
    fileName: sharedDocument.fileName,
    contentType: sharedDocument.contentType,
  });

  const handleDownload = async () => {
    if (!sharedDocument?.documentUrl || isDownloading) {
      return;
    }
    setIsDownloading(true);
    try {
      const response = await fetch(sharedDocument.documentUrl);
      if (!response.ok) {
        throw new Error(`Failed to download document. Status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobURL = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobURL;
      anchor.style.display = 'none';

      anchor.download = fileNameWithExtension?.length ? fileNameWithExtension : 'document';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(blobURL), 0);
    } catch (error) {
      console.error(error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div style={{ backgroundColor: sharedDocument.backgroundColor, minHeight: '100vh' }}>
      <div className='share-document-page main-content-wrapper px-3 px-lg-5 py-5 mx-auto' style={{ maxWidth: '920px' }}>
        <div className='d-flex align-items-end justify-content-between gap-15 mb-4 mb-lg-5'>
          <div className='flex-centered'>
            <img
              src={`${sharedDocument.organisationLogo}`}
              alt='Organisation logo'
              style={{ width: '40px' }}
              className='rounded'
            />
            <p className='mb-0 pl-3  title-h4-bold'>{sharedDocument.fileName}</p>
          </div>
          <button
            type='button'
            className='general-button specific-x-padding-20 specific-y-padding-7 border-general border-grey rounded-pill d-flex align-items-center hovered-button bg-main-white'
            disabled={isDownloading}
            onClick={handleDownload}
          >
            {isDownloading ? (
              <i className='fas fa-spinner fa-spin' />
            ) : (
              <>
                <span className='icon-download-attachment specific-right-margin-13' />
                Download
              </>
            )}
          </button>
        </div>
        <div className='share-document-preview border mx-auto'>
          {getFileExtension(fileNameWithExtension, sharedDocument.documentUrl) === 'pdf' ? (
            <SharedPdfPreview url={sharedDocument.documentUrl ?? ''} filename={fileNameWithExtension} />
          ) : (
            <AttachmentPreviewContent
              url={sharedDocument?.documentUrl}
              filename={fileNameWithExtension}
              pageNumber={undefined}
              onPageNumberChange={undefined}
              onPdfLoadSuccess={undefined}
              fullPdfPreview
              containedPreview
              shareDocumentPage={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SharedDocumentPreview;
